import { useState, useEffect } from "react";
import {
  ChevronDown, ChevronRight, Settings, Lock, UserCheck, Users, Cpu, Plus, Minus, Calendar,
} from "lucide-react";
import SenhaTecnicaModal from "./SenhaTecnicaModal";

// Paths que exigem senha técnica para serem acessados.
const PATHS_PROTEGIDOS = ["/cliente/coletores", "/cliente/cargos", "/cliente/cftv", "/cliente/lpr"];

// Senha técnica é guardada como timestamp (ms). Expira em 15min.
const SENHA_TECNICA_TTL_MS = 15 * 60 * 1000;
const SESSION_KEY = "brave_senha_tecnica_ts";

function senhaTecnicaAindaValida() {
  const ts = parseInt(sessionStorage.getItem(SESSION_KEY) || "0", 10);
  if (!ts) return false;
  if (Date.now() - ts > SENHA_TECNICA_TTL_MS) {
    sessionStorage.removeItem(SESSION_KEY);
    return false;
  }
  return true;
}

/**
 * Dropdown "Cadastros" da barra de navegação do cliente.
 * Contém: Admin (collapsible), Visitantes, Funcionários, Moradores e Controladores (collapsible).
 *
 * Estado de open/close é controlado pelo pai (para coordenar com outros dropdowns),
 * mas os collapsibles internos (Admin/Controladores) são state local.
 */
export default function NavCadastrosMenu({
  open,
  onMouseEnter,
  onMouseLeave,
  onToggle,
  onClose,
  navigate,
  isActive,
  isInPath,
  hasAdminPermission,
  hasControladoresPermission,
  adminSubItems,
  controladoresSubItems,
  visitantesSubItems,
  mostrarControladores = true,
  temMoradores,
  onAbrirAgendas,
  permMoradores = true,
  permFuncionarios = true,
  permVisitantes = true,
  permAgendas = true,
}) {
  const [adminOpen, setAdminOpen] = useState(false);
  const [controladoresOpen, setControladoresOpen] = useState(false);
  const [visitantesOpen, setVisitantesOpen] = useState(false);

  // Fecha todos os sub-collapsibles sempre que o dropdown principal Cadastros
  // fechar — assim, ao reabrir Cadastros, os sub-itens vêm sempre fechados.
  useEffect(() => {
    if (!open) {
      setAdminOpen(false);
      setControladoresOpen(false);
      setVisitantesOpen(false);
    }
  }, [open]);

  // Modal de Senha Técnica — abre antes de navegar para um path protegido.
  // Guarda timestamp em sessão; após 15min, exige nova validação.
  // Suporta dois fluxos: navegação por path (legado) e onClick (modais como Servidor).
  const [senhaTecnicaModal, setSenhaTecnicaModal] = useState({ open: false, path: "", label: "", onClick: null });

  const handleControladoresSubItemClick = (item) => {
    // Items podem ser protegidos por flag explícita (`item.protegido`) ou por path legado.
    const protegido = !!item.protegido || PATHS_PROTEGIDOS.includes(item.path);
    if (protegido && !senhaTecnicaAindaValida()) {
      setSenhaTecnicaModal({
        open: true,
        path: item.path || "",
        label: item.label,
        onClick: typeof item.onClick === "function" ? item.onClick : null,
      });
      return;
    }
    // Item com onClick custom (ex.: abre modal em vez de navegar)
    if (typeof item.onClick === "function") {
      item.onClick();
      onClose();
      return;
    }
    navigate(item.path);
    onClose();
  };

  return (
    <div
      className="relative nav-dropdown-container"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        data-testid="cliente-nav-cadastros"
        onClick={onToggle}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
          isInPath("/cliente/locais") || isInPath("/cliente/moradores") || isInPath("/cliente/funcionarios") ||
          isInPath("/cliente/departamentos") || isInPath("/cliente/portarias") || isInPath("/cliente/horarios") ||
          isInPath("/cliente/cargos") ||
          isInPath("/cliente/prestadores") || isInPath("/cliente/controladores") ||
          isInPath("/cliente/dispositivos") || isInPath("/cliente/visitantes") || isInPath("/cliente/operadores")
            ? "bg-white/15 text-white"
            : "text-brand-ice hover:bg-white/10 hover:text-white"
        }`}
      >
        Cadastros
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="cliente-cadastros-dropdown"
          className="absolute top-full left-0 mt-0 w-64 bg-brand-dark/95 backdrop-blur-md border border-brand-medium/30 rounded-lg shadow-xl overflow-hidden"
        >
          {/* Admin Collapsible */}
          <div>
            <button
              data-testid="cliente-nav-admin-toggle"
              onClick={(e) => {
                e.stopPropagation();
                if (hasAdminPermission) {
                  setAdminOpen(!adminOpen);
                }
              }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 ${
                !hasAdminPermission ? "text-brand-ice/70 cursor-not-allowed" :
                isInPath("/cliente/locais") || isInPath("/cliente/moradores") || isInPath("/cliente/funcionarios") ||
                isInPath("/cliente/departamentos") || isInPath("/cliente/portarias") || isInPath("/cliente/horarios") ||
                isInPath("/cliente/prestadores") || isInPath("/cliente/operadores")
                  ? "bg-white/15 text-white"
                  : "text-brand-ice hover:bg-white/10 hover:text-white"
              }`}
            >
              {hasAdminPermission ? <Settings className="w-4 h-4" /> : <Lock className="w-4 h-4 text-yellow-500" />}
              <span className="flex-1 text-left">Admin</span>
              {hasAdminPermission && (adminOpen ? (
                <ChevronDown className="w-3.5 h-3.5" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              ))}
            </button>

            {adminOpen && hasAdminPermission && (
              <div className="bg-black/20">
                {adminSubItems.map((item) => (
                  <button
                    key={item.path}
                    data-testid={`cliente-nav-${item.path.split("/").pop()}`}
                    onClick={() => { navigate(item.path); onClose(); }}
                    className={`w-full flex items-center gap-3 pl-8 pr-4 py-2 text-sm transition-colors duration-150 ${
                      isActive(item.path) ? "bg-white/15 text-white" : "text-brand-ice/80 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Usuários Fixos — Moradores, Funcionários e Terceiros unificados */}
          {(permMoradores || permFuncionarios) && (
            <button
              data-testid="cliente-nav-usuarios-fixos"
              onClick={() => { navigate("/cliente/moradores"); onClose(); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 border-t border-brand-ice/10 ${
                isInPath("/cliente/moradores") || isInPath("/cliente/funcionarios") || isInPath("/cliente/terceiros")
                  ? "bg-white/15 text-white"
                  : "text-brand-ice hover:bg-white/10 hover:text-white"
              }`}
            >
              <Users className="w-4 h-4" />
              Usuários Fixos
            </button>
          )}

          {/* Agendas (abre modal "Agendas de Hoje") — logo acima de Visitantes */}
          {permAgendas && (
            <button
              data-testid="cliente-nav-agendas"
              onClick={() => {
                if (typeof onAbrirAgendas === "function") onAbrirAgendas();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 border-t border-brand-ice/10 text-brand-ice hover:bg-white/10 hover:text-white"
            >
              <Calendar className="w-4 h-4" />
              Agendas
            </button>
          )}

          {/* Visitantes (collapsible com cadastros auxiliares) */}
          {permVisitantes && (
          <div className="border-t border-brand-ice/10">
            <div className="flex">
              <button
                data-testid="cliente-nav-visitantes"
                onClick={() => { navigate("/cliente/visitantes/novo"); onClose(); }}
                className={`flex-1 flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 ${
                  isActive("/cliente/visitantes/novo") || isActive("/cliente/visitantes") ? "bg-white/15 text-white" : "text-brand-ice hover:bg-white/10 hover:text-white"
                }`}
              >
                <UserCheck className="w-4 h-4" />
                <span className="flex-1 text-left">Visitantes</span>
              </button>
              <button
                data-testid="cliente-nav-visitantes-toggle"
                onClick={(e) => {
                  e.stopPropagation();
                  setVisitantesOpen(!visitantesOpen);
                }}
                aria-label="Abrir cadastros relacionados a Visitantes"
                className="px-3 text-brand-ice hover:bg-white/10 hover:text-white transition-colors duration-150"
              >
                {visitantesOpen ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
              </button>
            </div>

            {visitantesOpen && (
              <div className="bg-black/20">
                {(visitantesSubItems || []).map((item) => (
                  <button
                    key={item.path}
                    data-testid={`cliente-nav-${item.path.split("/").pop()}`}
                    onClick={() => { navigate(item.path); onClose(); }}
                    className={`w-full flex items-center gap-3 pl-8 pr-4 py-2 text-sm transition-colors duration-150 ${
                      isActive(item.path) ? "bg-white/15 text-white" : "text-brand-ice/80 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Controladores Collapsible — oculto se admin master desabilitou */}
          {mostrarControladores && (
          <div className="border-t border-brand-ice/10">
            <button
              data-testid="cliente-nav-controladores-toggle"
              onClick={(e) => {
                e.stopPropagation();
                if (hasControladoresPermission) {
                  setControladoresOpen(!controladoresOpen);
                }
              }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 ${
                !hasControladoresPermission ? "text-brand-ice/70 cursor-not-allowed" :
                isInPath("/cliente/controladores") || isInPath("/cliente/dispositivos") || isInPath("/cliente/cargos") || isInPath("/cliente/coletores") || isInPath("/cliente/cftv")
                  ? "bg-white/15 text-white"
                  : "text-brand-ice hover:bg-white/10 hover:text-white"
              }`}
            >
              {hasControladoresPermission ? <Cpu className="w-4 h-4" /> : <Lock className="w-4 h-4 text-yellow-500" />}
              <span className="flex-1 text-left">Controladores</span>
              {hasControladoresPermission && (controladoresOpen ? (
                <ChevronDown className="w-3.5 h-3.5" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              ))}
            </button>

            {controladoresOpen && hasControladoresPermission && (
              <div className="bg-black/20">
                {controladoresSubItems.map((item) => (
                  <button
                    key={item.path || item.label}
                    data-testid={item.path
                      ? `cliente-nav-${item.path.split("/").pop()}`
                      : `cliente-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => handleControladoresSubItemClick(item)}
                    className={`w-full flex items-center gap-3 pl-8 pr-4 py-2 text-sm transition-colors duration-150 ${
                      item.path && isActive(item.path) ? "bg-white/15 text-white" : "text-brand-ice/80 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <item.icon className="w-4 h-4" />
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          )}
        </div>
      )}

      {/* Modal de validação de Senha Técnica */}
      <SenhaTecnicaModal
        open={senhaTecnicaModal.open}
        targetLabel={senhaTecnicaModal.label}
        onClose={() => setSenhaTecnicaModal({ open: false, path: "", label: "", onClick: null })}
        onSuccess={() => {
          // Grava timestamp da validação. Expira em 15min.
          sessionStorage.setItem(SESSION_KEY, String(Date.now()));
          const dest = senhaTecnicaModal.path;
          const cb = senhaTecnicaModal.onClick;
          setSenhaTecnicaModal({ open: false, path: "", label: "", onClick: null });
          // Itens com onClick (ex.: modal Servidor) têm prioridade sobre navegação.
          if (typeof cb === "function") {
            cb();
            onClose();
          } else if (dest) {
            navigate(dest);
            onClose();
          }
        }}
      />
    </div>
  );
}
